import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Mark from "../components/Mark";

// The homepage.
//
// One claim, made concretely: other tools generate a demo, Atelier generates
// something you would put a customer's data in. Every section exists to make
// that specific rather than boastful — the file counts are real, the gate list
// is the actual gate list, and the "what it refuses" section is the honest one
// that most landing pages leave out.
//
// The 3D scene is LAZY. It pulls in three.js, and a visitor on a phone should
// get readable copy immediately rather than waiting on a 600 KB chunk for a
// decoration.
const Assembly = lazy(() => import("../three/Assembly"));

/** Reveals children once, when they first scroll into view. One observer per
 *  element, disconnected on first hit — cheaper than a scroll listener and it
 *  never fights the browser's own scheduling. */
function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "-12% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-[900ms] ease-out ${
        shown ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Nav() {
  return (
    <header className="fixed inset-x-0 top-0 z-30">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-5">
        <Link to="/" className="flex items-center gap-2.5">
          <Mark className="h-7 w-7" />
          <span className="text-[0.95rem] font-semibold tracking-tight">Atelier</span>
        </Link>
        <span className="ml-1 hidden text-2xs text-muted sm:inline">by Symphia</span>
        <div className="ml-auto flex items-center gap-1">
          <Link
            to="/signin"
            className="rounded-card px-4 py-2 text-sm text-muted transition hover:text-ink"
          >
            Sign in
          </Link>
          <Link
            to="/signin"
            className="rounded-card bg-ink px-4 py-2 text-sm font-medium text-paper transition hover:opacity-85"
          >
            Start building
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Home() {
  return (
    <div className="relative">
      <Nav />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative flex min-h-[100svh] items-center overflow-hidden pb-24 pt-20 sm:pb-40">
        <Suspense fallback={null}>
          <Assembly className="absolute inset-0 z-0" />
        </Suspense>

        {/* A paper wash under the copy so the type stays legible over the
            scene at any camera angle, without dimming the scene itself. */}
        <div
          aria-hidden
          className="absolute inset-0 z-10"
          style={{
            background:
              "radial-gradient(60% 46% at 50% 38%, rgb(var(--paper) / 0.95) 0%, rgb(var(--paper) / 0.78) 48%, rgb(var(--paper) / 0.08) 84%, transparent 100%)",
          }}
        />
        {/* Edge vignette — the sculpture should fade into the page rather than
            being cropped by the viewport. */}
        <div
          aria-hidden
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(to bottom, rgb(var(--paper)) 0%, transparent 18%, transparent 78%, rgb(var(--paper)) 100%)",
          }}
        />

        <div className="relative z-20 mx-auto mb-auto mt-[18vh] w-full max-w-4xl px-6 text-center">
          {/* A halo bound to the COPY, not to the viewport.
              The hero wash alone is a fixed radial, so on a short or wide
              window the structure rose into the paragraph and ate it. This
              travels with the text column, so the copy is legible whatever the
              scene is doing behind it. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -inset-y-10 -z-10"
            style={{
              background:
                "radial-gradient(closest-side, rgb(var(--paper) / 0.97) 0%, rgb(var(--paper) / 0.9) 55%, transparent 100%)",
            }}
          />
          <Reveal>
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-hairline bg-paper/70 px-3.5 py-1.5 text-2xs text-muted backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-clay" />
              Go · Postgres · React · SwiftUI · Compose — deployed
            </p>
          </Reveal>

          <Reveal delay={80}>
            <h1 className="text-balance text-[clamp(2.6rem,7vw,5rem)] font-semibold leading-[1.02] tracking-[-0.03em]">
              Describe an app.
              <br />
              Get a <span className="text-clay">production</span> one.
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p className="mx-auto mt-7 max-w-xl text-balance text-[1.05rem] leading-relaxed text-muted">
              Not a prototype you rewrite. A complete application — backend, provisioned database,
              web, mobile — that compiles, passes its own tests, and deploys behind a health gate
              that rolls itself back.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/signin"
                className="rounded-card bg-ink px-6 py-3 text-sm font-medium text-paper transition hover:opacity-85"
              >
                Build something
              </Link>
              <a
                href="#how"
                className="rounded-card border border-hairline bg-paper/70 px-6 py-3 text-sm backdrop-blur transition hover:border-clay/60"
              >
                How it works
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── What actually comes out ─────────────────────────────────────── */}
      <section className="relative z-20 border-t border-hairline bg-paper">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <p className="text-2xs uppercase tracking-[0.2em] text-muted">One prompt produces</p>
            <h2 className="mt-3 max-w-2xl text-balance text-[clamp(1.7rem,3.6vw,2.6rem)] font-semibold leading-tight tracking-tight">
              Eighty-six files across four surfaces, from eight words.
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-px overflow-hidden rounded-card border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                k: "Backend",
                n: "Go · Gin · GORM",
                d: "Postgres in production, SQLite in dev. Structured logging, Prometheus, graceful shutdown.",
              },
              {
                k: "Web",
                n: "React · TypeScript",
                d: "Vite, Tailwind, TanStack Query, and an API client typed from the same spec as the server.",
              },
              {
                k: "iOS",
                n: "SwiftUI",
                d: "Codables that mirror the wire exactly, because they are generated in the same pass.",
              },
              {
                k: "Android",
                n: "Kotlin · Compose",
                d: "Material 3 and Ktor, with kotlinx-serialization tolerant of fields it has not seen.",
              },
            ].map((s, i) => (
              <Reveal key={s.k} delay={i * 70}>
                <div className="h-full bg-paper p-6">
                  <p className="text-2xs uppercase tracking-[0.16em] text-clay">{s.k}</p>
                  <p className="mt-2 text-sm font-medium">{s.n}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── The idea ────────────────────────────────────────────────────── */}
      <section id="how" className="relative z-20 border-t border-hairline bg-raised">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <p className="text-2xs uppercase tracking-[0.2em] text-muted">How it works</p>
            <h2 className="mt-3 max-w-3xl text-balance text-[clamp(1.7rem,3.6vw,2.6rem)] font-semibold leading-tight tracking-tight">
              The model designs your data model. It never writes your infrastructure.
            </h2>
            <p className="mt-5 max-w-2xl text-[1.02rem] leading-relaxed text-muted">
              That split is the whole product. Everything that makes an app production-grade is
              hand-written, reviewed code that is emitted identically every time. The model only
              fills business logic into function bodies whose signatures are already fixed.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-card border border-hairline bg-paper p-7">
                <p className="text-2xs uppercase tracking-[0.16em] text-muted">The chassis</p>
                <p className="mt-2 text-lg font-medium">Hand-written. Deterministic.</p>
                <p className="mt-3 text-sm leading-relaxed text-muted">
                  The Gin bootstrap, migrations, auth, pagination, the error envelope, the
                  Prometheus chokepoints, the systemd unit, the CI pipeline, the health gate, the
                  rollback. The same blueprint emits byte-identical files, so two customers' apps
                  have the same observability because they are literally the same bytes.
                </p>
              </div>
            </Reveal>

            <Reveal delay={90}>
              <div className="h-full rounded-card border border-clay/30 bg-clay/[0.04] p-7">
                <p className="text-2xs uppercase tracking-[0.16em] text-clay">The domain</p>
                <p className="mt-2 text-lg font-medium">Written by the model. Compile-gated.</p>
                <p className="mt-3 text-sm leading-relaxed text-muted">
                  Four function bodies per entity — validation, derived fields, referential guards.
                  Each file is compiled on its own, and one that fails is reverted to a default
                  that was already correct. So this stage can improve an app. It cannot break one.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={140}>
            <div className="mt-10 overflow-x-auto rounded-card border border-hairline bg-ink px-7 py-6">
              <pre className="logbox text-paper/85">{`  ── verify ────────────────────────────────────────────
    go vet ./...        ok
    go build ./...      ok
    go test ./...       ok      ← the app's own contract tests
    tsc --noEmit        ok
    vite build          ok

  BUILD PASSED in 12s — 58 files, 3 written by the model.`}</pre>
            </div>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              A build that fails any applicable gate is failed and refunded. The tests it runs are
              the ones asserting the promises: that the list limit is capped, that the sort
              parameter cannot reach <code className="font-mono text-ink">ORDER BY</code>, that
              metric labels carry no row ids, that production refuses to boot on a default secret.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Deployment ──────────────────────────────────────────────────── */}
      <section className="relative z-20 border-t border-hairline bg-paper">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <p className="text-2xs uppercase tracking-[0.2em] text-muted">And then it ships</p>
            <h2 className="mt-3 max-w-3xl text-balance text-[clamp(1.7rem,3.6vw,2.6rem)] font-semibold leading-tight tracking-tight">
              Its own database. Its own release. Its own way back.
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              {
                t: "A database, not a shared table",
                d: "Every app gets its own Postgres database and a scoped role, with CONNECT revoked from PUBLIC — because Postgres grants it by default, and without that revoke every app could read every other one.",
              },
              {
                t: "A release, not an overwrite",
                d: "Ships to a new release directory and moves a symlink. The previous release stays on disk, so undoing a bad deploy is seconds rather than a rebuild.",
              },
              {
                t: "A gate that cannot be fooled",
                d: "It polls /health, never systemctl. A binary that panics on boot reads as “active” at exactly the wrong moment — so if health does not answer, the symlink swings back automatically.",
              },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 80}>
                <div>
                  <div className="mb-4 h-px w-10 bg-clay" />
                  <p className="text-base font-medium">{c.t}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── The honest section ──────────────────────────────────────────── */}
      <section className="relative z-20 border-t border-hairline bg-raised">
        <div className="mx-auto max-w-3xl px-6 py-24">
          <Reveal>
            <p className="text-2xs uppercase tracking-[0.2em] text-muted">What it will not do</p>
            <h2 className="mt-3 text-balance text-[clamp(1.5rem,3vw,2.1rem)] font-semibold leading-tight tracking-tight">
              The limits, stated plainly.
            </h2>
            <ul className="mt-8 flex flex-col gap-5">
              {[
                "iOS and Android projects are generated, not compiled — that needs Xcode and the Android SDK. Their models come from the same validated spec as the verified Go and TypeScript, so the contract is right by construction, but we only claim “compiles” about what was compiled.",
                "It is not a design tool. Every app gets a coherent theme derived from its domain; none of them will win an award.",
                "It will not invent your business rules. It writes the ones a careful engineer would expect from the domain, and leaves the rest to you — in a file that regeneration preserves.",
              ].map((l, i) => (
                <li key={i} className="flex gap-4 text-sm leading-relaxed text-muted">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-clay" />
                  {l}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="relative z-20 border-t border-hairline bg-paper">
        <div className="mx-auto max-w-3xl px-6 py-28 text-center">
          <Reveal>
            <Mark className="mx-auto h-10 w-10" />
            <h2 className="mt-6 text-balance text-[clamp(1.8rem,4vw,2.8rem)] font-semibold leading-tight tracking-tight">
              What are you building?
            </h2>
            <p className="mx-auto mt-4 max-w-md text-[1.02rem] leading-relaxed text-muted">
              Describe it in a sentence. Planning is free — you see the whole data model before you
              spend anything.
            </p>
            <Link
              to="/signin"
              className="mt-8 inline-block rounded-card bg-ink px-7 py-3.5 text-sm font-medium text-paper transition hover:opacity-85"
            >
              Start building
            </Link>
          </Reveal>
        </div>
      </section>

      <footer className="relative z-20 border-t border-hairline bg-paper">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-6 py-8 text-2xs text-muted">
          <span className="flex items-center gap-2">
            <Mark className="h-4 w-4" /> Atelier by Symphia
          </span>
          <span className="ml-auto">
            Every generated app ships with health checks, metrics, CI and an automatic rollback.
          </span>
        </div>
      </footer>
    </div>
  );
}
